import { volateqApi } from "@/app/shared/services/volateq-api/volateq-api";
import { NotificationSchema } from "@/app/shared/services/volateq-api/api-schemas/notification-schema";
import { store } from "@/app/shared/stores/store";
import { AUTH_TOKEN_CHANGED_EVENT } from "@/app/shared/stores/auth";
import type { AuthState } from "@/app/shared/stores/auth";
import { NotificationLeader } from "@/app/shared/services/helper/notification-leader";
import type { WorkerToTabMessage } from "@/app/shared/services/realtime/realtime-worker";
import { Channel } from "../types";
import { BaseChannel } from "./base-channel";

/** Broadcast key used to propagate setCount() across all open tabs via the realtime worker. */
const COUNT_BROADCAST_KEY = "notification-unread-count";

/** Broadcast key used by the primary tab to share freshly fetched notifications with all tabs. */
const NEW_NOTIFICATIONS_BROADCAST_KEY = "notification-new";

/** Upper bound for the duplicate-delivery guard so the set cannot grow unbounded. */
const MAX_HANDLED_NOTIFICATION_IDS = 500;

/**
 * Safety net for a worker that reports neither a connect nor a connect error, e.g. a
 * handshake the server accepts but never answers. Normally the worker reports the socket
 * state on its own (see BaseChannel.isRealtimeReachable).
 */
const REALTIME_CONNECT_TIMEOUT_MS = 5000;

/** Interval of the HTTP fallback, used for as long as the realtime socket is unreachable. */
const HTTP_POLL_INTERVAL_MS = 60000;

/** Cap on the notifications one fallback poll surfaces, so a long absence cannot flood the tab. */
const MAX_POLLED_NEW_NOTIFICATIONS = 10;

enum NotificationEvent {
  Created = "NOTIFICATION.CREATED",
  Read = "NOTIFICATION.READ",
}

interface NotificationCreatedPayload {
  notification_id: string;
  user_id: string;
}

export interface NotificationPollUpdate {
  count: number;
  /** Newly arrived notifications pushed via realtime (most recent first); empty otherwise. */
  newNotifications: NotificationSchema[];
}

type NotificationPollListener = (update: NotificationPollUpdate) => void;

/**
 * Tab-wide singleton channel that tracks the unread-notification count.
 * Replaces both the old NotificationChannel + NotificationPollService pair.
 *
 * - Delegates the socket.io connection to the shared `realtime-worker` SharedWorker.
 * - On NOTIFICATION.CREATED: the primary tab fetches the notification bodies and
 *   broadcasts them to every tab, so each tab can surface them (see notify() in
 *   the bell component for who shows what).
 * - On NOTIFICATION.READ: the backend read notifications on the user's behalf, so
 *   the primary tab re-syncs the count.
 * - setCount() broadcasts the new count to all tabs via the worker.
 * - While the socket cannot be reached, an HTTP poll takes over (see startHttpFallback).
 */
export class NotificationChannel extends BaseChannel {
  readonly channelName = Channel.NOTIFICATION;

  private static instance: NotificationChannel | null = null;

  public static getInstance(): NotificationChannel {
    if (!NotificationChannel.instance) {
      NotificationChannel.instance = new NotificationChannel();
    }
    return NotificationChannel.instance;
  }

  private pollListeners = new Set<NotificationPollListener>();
  private unreadCount = 0;

  /** Ids from CREATED events whose bodies still have to be fetched (primary tab only). */
  private pendingNotificationIds = new Set<string>();

  /** Guards against the same CREATED event being delivered more than once. */
  private handledNotificationIds = new Set<string>();

  /** True once the first "connected" arrived; later ones indicate a reconnect. */
  private hasReceivedConnected = false;

  /** Set while the HTTP fallback runs, i.e. for as long as the socket is unreachable. */
  private httpPollTimer: ReturnType<typeof setInterval> | null = null;

  private constructor() {
    super();

    // Elect a single primary tab so that only one open tab fetches notification
    // bodies and surfaces the OS notification, no matter how many tabs are open.
    NotificationLeader.start();

    void this.userId.then(id => this.onConnected(id));
    void this.startHttpFallback();

    this.port.addEventListener("message", (event: MessageEvent<WorkerToTabMessage>) => {
      const msg = event.data;
      if (msg.type === "connected") {
        this.stopHttpFallback();

        // The worker announces every (re)connect. Notifications created while
        // the socket was down were never delivered — re-sync the count. Only
        // the primary tab fetches; setCount() broadcasts it to the others.
        if (this.hasReceivedConnected && NotificationLeader.isPrimary) {
          void this.resyncCount();
        }
        this.hasReceivedConnected = true;
      } else if (msg.type === "unavailable") {
        // The socket went down after it had been up — take over with HTTP again.
        void this.startHttpFallback();
      } else if (msg.type === "event" && msg.eventName === NotificationEvent.Created) {
        this.onNotificationCreated((msg.payload as NotificationCreatedPayload | undefined)?.notification_id);
      } else if (msg.type === "event" && msg.eventName === NotificationEvent.Read) {
        // The backend marked notifications read on its own (e.g. an analysis was finished).
        // Only the primary tab fetches; setCount() broadcasts the result to the others.
        if (NotificationLeader.isPrimary) {
          void this.resyncCount();
        }
      } else if (msg.type === "broadcast" && msg.key === COUNT_BROADCAST_KEY) {
        const count = msg.payload as number;
        if (count !== this.unreadCount) {
          this.unreadCount = count;
          this.emit([]);
        }
      } else if (msg.type === "broadcast" && msg.key === NEW_NOTIFICATIONS_BROADCAST_KEY) {
        // Sent by the primary tab (and echoed back to it) so every tab can
        // surface the new notifications, not just the one holding the lock.
        this.emit(msg.payload as NotificationSchema[]);
      }
    });

    // Cross-tab: another tab wrote a new (or empty) token to localStorage.
    // Parse the event value directly instead of reading the store, which may
    // not have processed the same storage event yet.
    window.addEventListener("storage", (event: StorageEvent) => {
      if (event.key !== "auth_token") return;
      let token = "";
      try {
        token = event.newValue ? ((JSON.parse(event.newValue) as AuthState).token ?? "") : "";
      } catch {
        token = store.auth.token;
      }
      this.pushCredentials(token);
    });

    // Same-tab: storage events never fire in the tab that changed the value,
    // so token refresh/login/logout in this tab must be pushed explicitly.
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, () => this.pushCredentials());
  }

  /** Subscribe a UI listener to count/notification updates. Returns an unsubscribe function. */
  public subscribe(listener: NotificationPollListener): () => void {
    this.pollListeners.add(listener);
    listener({ count: this.unreadCount, newNotifications: [] });
    return () => {
      this.pollListeners.delete(listener);
    };
  }

  /** Push a locally-known count (e.g. after marking all read) to all tabs without a round-trip. */
  public setCount(count: number): void {
    this.unreadCount = count;
    this.broadcastToTabs(COUNT_BROADCAST_KEY, count);
    this.emit([]);
  }

  private onNotificationCreated(notificationId: string | undefined): void {
    // A reconnecting socket can deliver the same event twice — count and
    // surface each notification only once.
    if (notificationId) {
      if (this.handledNotificationIds.has(notificationId)) {
        return;
      }
      this.handledNotificationIds.add(notificationId);
      this.trimHandledIds();
    }

    this.unreadCount += 1;
    this.emit([]);

    if (!NotificationLeader.isPrimary) {
      return;
    }
    if (notificationId) {
      this.pendingNotificationIds.add(notificationId);
    }
    void this.fetchAndBroadcastNew().catch(e => {
      console.error("Could not receive notifications:", e);
    });
  }

  /**
   * Fetches the bodies of all pending CREATED events in one request and shares
   * them with every tab. Fetching by the pending ids (instead of just "latest
   * 1") makes sure that two notifications arriving in quick succession are both
   * surfaced instead of the newest one twice.
   */
  private async fetchAndBroadcastNew(): Promise<void> {
    const limit = Math.min(Math.max(this.pendingNotificationIds.size, 1), 50);
    const latestUnread = await volateqApi.getNotifications(true, 0, limit);

    const newNotifications = this.pendingNotificationIds.size > 0
      ? latestUnread.filter(notification => this.pendingNotificationIds.delete(notification.id))
      : latestUnread.slice(0, 1);

    if (newNotifications.length > 0) {
      this.broadcastToTabs(NEW_NOTIFICATIONS_BROADCAST_KEY, newNotifications);
    }
  }

  private trimHandledIds(): void {
    // Sets iterate in insertion order — drop the oldest entries.
    while (this.handledNotificationIds.size > MAX_HANDLED_NOTIFICATION_IDS) {
      const oldest = this.handledNotificationIds.values().next().value;
      if (oldest === undefined) {
        return;
      }
      this.handledNotificationIds.delete(oldest);
    }
  }

  /**
   * A silent bell is indistinguishable from an empty one: no badge and no popup looks
   * exactly like "nothing happened". So whenever the socket cannot be reached — a rejected
   * handshake, a non-admin user, no realtime server in local dev — keep the bell alive
   * over HTTP instead of leaving the count to the next time the panel is opened.
   *
   * The reachability is checked before the timer, so that a fallback already running is
   * recognized in the same turn it was started in: the "unavailable" that makes the
   * constructor's call resolve reaches the message handler as well.
   */
  private async startHttpFallback(): Promise<void> {
    if (await this.isRealtimeReachable(REALTIME_CONNECT_TIMEOUT_MS) || this.httpPollTimer !== null) {
      return;
    }

    // Only the primary tab keeps polling; setCount() shares what it finds with all the
    // others. Every tab still reads the count once, so its badge is right immediately.
    this.httpPollTimer = setInterval(() => {
      if (NotificationLeader.isPrimary) {
        void this.pollUnread(false);
      }
    }, HTTP_POLL_INTERVAL_MS);

    await this.pollUnread(true);
  }

  private stopHttpFallback(): void {
    if (this.httpPollTimer === null) {
      return;
    }
    clearInterval(this.httpPollTimer);
    this.httpPollTimer = null;
  }

  /**
   * One fallback poll: the count for the badge, plus the notifications that arrived since
   * the last one so they are still surfaced. The first poll only takes the count: what is
   * unread when a tab opens was already there, and must not arrive as a popup each.
   */
  private async pollUnread(isFirstPoll: boolean): Promise<void> {
    try {
      const previousCount = this.unreadCount;
      const { count } = await volateqApi.getUnreadNotificationCount();
      this.setCount(count);

      if (isFirstPoll || count <= previousCount) {
        return;
      }

      const limit = Math.min(count - previousCount, MAX_POLLED_NEW_NOTIFICATIONS);
      const latestUnread = await volateqApi.getNotifications(true, 0, limit);

      const newNotifications = latestUnread.filter(
        notification => !this.handledNotificationIds.has(notification.id),
      );
      newNotifications.forEach(notification => this.handledNotificationIds.add(notification.id));
      this.trimHandledIds();

      if (newNotifications.length > 0) {
        this.broadcastToTabs(NEW_NOTIFICATIONS_BROADCAST_KEY, newNotifications);
      }
    } catch (e) {
      console.error("Failed to poll notifications:", e);
    }
  }

  private async resyncCount(): Promise<void> {
    try {
      const { count } = await volateqApi.getUnreadNotificationCount();
      this.setCount(count);
    } catch (e) {
      console.error("Failed to re-sync notification count:", e);
    }
  }

  private pushCredentials(token: string = store.auth.token): void {
    this.port.postMessage({
      type: "updateCredentials",
      token,
      botProtectionToken: store.protection.botProtectionToken,
    });
  }

  private async onConnected(userId: string): Promise<void> {
    await this.joinChannel(userId);
    await this.fetchInitialCount();
  }

  private async fetchInitialCount(): Promise<void> {
    try {
      const { count } = await volateqApi.getUnreadNotificationCount();
      this.unreadCount = count;
      this.emit([]);
    } catch (e) {
      // Network unavailable — keep the cached count.
      console.error("Failed to fetch initial notifications count:", e);
    }
  }

  private emit(newNotifications: NotificationSchema[]): void {
    const update: NotificationPollUpdate = { count: this.unreadCount, newNotifications };
    this.pollListeners.forEach(listener => listener(update));
  }
}
